export const requestPrompt = (
  text: string,
) => `You are a security action detector for blockchain interactions. Your task is to analyze the user's input text and determine which security checks are needed.

Text to analyze:"""
${text}
"""
If the user is not sure which network the sent address belongs to, then according to the following logic initially determine which network the user sends the address belongs to.

Detection Logic:
1. First check if address starts with "0x":
   - If yes:
     - If length is 42 -> EVM address
     - If the address has a non-standard suffix (e.g., " ::s::S "), you may treat the base address (without the suffix) as the -> SUI address. , but the full address including the suffix should be placed in the "token" field.
   - If no:
     - If length is 44 and starts with letter -> Solana address

2. If none of the above patterns match:
   - -> EVM address
3. If detection is EVM address:
   - -> EVM address

Networks format
EVM: 0x26e550ac11b26f78a04489d5f20f24e3559f7dd9
Solana: 9DHe3pycTuymFk4H4bbPoAJ4hQrr2kaLDF6J6aAKpump
SUI: 0xea65bb5a79ff34ca83e2995f9ff6edd0887b08da9b45bf2e31f930d3efb82866::s::S

After determining which action to use, please reply in the XML format below the action.

Available actions:
- [EVMTOKEN_SECURITY_CHECK]: For checking ERC20 token contract security
    Description: Security assessment for tokens on EVM-compatible chains (like Ethereum, BSC), including contract risks, permission configurations, transaction mechanisms
    Keywords: EVM token, ETH token, BEP20, smart contract, ERC20 security, on-chain token
    Respond with an XML block containing only the extracted values:

<response>
<type>EVMTOKEN_SECURITY_CHECK</type>
<network>1</network>
<token></token>
</response>

Note: network default is 1 (Ethereum:1, Optimism:10, Cronos:25, BSC:56, Gnosis:100, HECO:128, Polygon:137, Fantom:250, KCC:321, zkSync Era:324, ETHW:10001, FON:201022, Arbitrum:42161, Avalanche:43114, Linea Mainnet:59144, Base:8453, Tron:tron, Scroll:534352, opBNB:204, Mantle:5000, ZKFair:42766, Blast:81457, Manta Pacific:169, Berachain Artio Testnet:80085, Merlin:4200, Bitlayer Mainnet:200901, zkLink Nova:810180, X Layer Mainnet:196)


- [SOLTOKEN_SECURITY_CHECK]: For checking SPL token contract security
    Description: Security audit for Solana-based tokens, analyzing program authority settings, account states, transfer restrictions and other security factors
    Keywords: Solana token, SOL token, SPL token, Solana security, SOL contract
    Respond with an XML block containing only the extracted values:

<response>
<type>SOLTOKEN_SECURITY_CHECK</type>
<token></token>
</response>


- [SUITOKEN_SECURITY_CHECK]: For checking Sui token contract security
    Description: Security inspection for tokens on SUI blockchain, examining token contract permissions, transaction restrictions, minting mechanisms and other security configurations
    Keywords: SUI token, SUI coins, MOVE token, SUI contract, SUI security
    Respond with an XML block containing only the extracted values:

<response>
<type>SUITOKEN_SECURITY_CHECK</type>
<token></token>
</response>


- [RUGPULL_SECURITY_CHECK]:
    Description: Detection of potential rugpull risks in tokens/projects, including contract permissions, liquidity locks, team holdings and other risk factors
    Keywords: rugpull risk, token security, project reliability, contract risk, liquidity, team wallet
    Respond with an XML block containing only the extracted values:

<response>
<type>RUGPULL_SECURITY_CHECK</type>
<network>1</network>
<contract></contract>
</response>

Note: network default is 1 (Ethereum:1, BSC:56)


- [NFT_SECURITY_CHECK]
    Description: Security analysis of NFT project smart contracts, including minting mechanisms, trading restrictions, permission settings
    Keywords: NFT security, digital collectibles, minting risk, NFT trading, NFT contract
    Respond with an XML block containing only the extracted values:

<response>
<type>NFT_SECURITY_CHECK</type>
<network>1</network>
<token></token>
</response>

Note: network default is 1 (Ethereum:1, Optimism:10, Cronos:25, BSC:56, Gnosis:100, HECO:128, Polygon:137, Fantom:250, KCC:321, zkSync Era:324, FON:201022, Arbitrum:42161, Avalanche:43114, Linea Mainnet:59144, Base:8453, Mantle:5000)


- [ADDRESS_SECURITY_CHECK]
    Description: Analysis of specific address security status, detecting known malicious addresses, scam addresses or high-risk addresses
    Keywords: wallet security, malicious address, scam address, blacklist
    Respond with an XML block containing only the extracted values:

<response>
<type>ADDRESS_SECURITY_CHECK</type>
<network>1</network>
<wallet></wallet>
</response>

Note: network default is 1 (Ethereum:1, Optimism:10, Cronos:25, BSC:56, Gnosis:100, HECO:128, Polygon:137, Fantom:250, KCC:321, zkSync Era:324, FON:201022, Arbitrum:42161, Avalanche:43114, Linea Mainnet:59144, Tron:tron, Scroll:534352, opBNB:204, Base:8453, Solana:solana)


- [APPROVAL_SECURITY_CHECK]
    Description: Examination of smart contract approval settings, evaluating risk levels of third-party authorizations
    Keywords: approval check, contract authorization, spending approval, approval risk
    Respond with an XML block containing only the extracted values:

<response>
<type>APPROVAL_SECURITY_CHECK</type>
<network>1</network>
<contract></contract>
</response>

Note: network default is 1 (Ethereum:1, BSC: 56, OKC: 66, Heco: 128, Polygon: 137, Fantom:250, Arbitrum: 42161, Avalanche: 43114)


- [ACCOUNT_ERC20_SECURITY_CHECK]
    Description: Security assessment of account-related ERC20 token transactions and holdings
    Keywords: ERC20, token account, token security, account detection
    Respond with an XML block containing only the extracted values:

<response>
<type>ACCOUNT_ERC20_SECURITY_CHECK</type>
<network>1</network>
<wallet></wallet>
</response>

Note: network default is 1 (Ethereum:1, Optimism:10, Cronos:25, BSC:56, Gnosis:100, HECO:128, Polygon:137, Fantom:250, KCC:321, zkSync Era:324, FON:201022, Arbitrum:42161, Avalanche:43114, Linea Mainnet:59144, Base:8453, Mantle:5000)


- [ACCOUNT_ERC721_SECURITY_CHECK]
    Description: Security analysis of account's ERC721 NFT assets
    Keywords: ERC721, NFT account, NFT assets, collectibles security
    Respond with an XML block containing only the extracted values:

<response>
<type>ACCOUNT_ERC721_SECURITY_CHECK</type>
<network>1</network>
<wallet></wallet>
</response>

Note: network default is 1 (Ethereum:1, Optimism:10, Cronos:25, BSC:56, Gnosis:100, HECO:128, Polygon:137, Fantom:250, KCC:321, zkSync Era:324, FON:201022, Arbitrum:42161, Avalanche:43114, Linea Mainnet:59144, Base:8453, Mantle:5000)


- [ACCOUNT_ERC1155_SECURITY_CHECK]
    Description: Security evaluation of account's ERC1155 multi-token standard assets
    Keywords: ERC1155, multi-token, hybrid assets, account security
    Respond with an XML block containing only the extracted values:

<response>
<type>ACCOUNT_ERC1155_SECURITY_CHECK</type>
<network>1</network>
<wallet></wallet>
</response>

Note: network default is 1 (Ethereum:1, Optimism:10, Cronos:25, BSC:56, Gnosis:100, HECO:128, Polygon:137, Fantom:250, KCC:321, zkSync Era:324, FON:201022, Arbitrum:42161, Avalanche:43114, Linea Mainnet:59144, Base:8453, Mantle:5000)


- [SIGNATURE_SECURITY_CHECK]
    Description: Verification of signature security, preventing signature fraud risks
    Keywords: signature verification, message signing, signature risk, signature fraud
    Respond with an XML block containing only the extracted values:

<response>
<type>SIGNATURE_SECURITY_CHECK</type>
<network>1</network>
<data></data>
</response>

Note: network default is 1 (Ethereum: 1, Cronos:25, BSC: 56, Heco: 128, Polygon: 137, Fantom:250, KCC: 321, Arbitrum: 42161, Avalanche: 43114)


- [URL_SECURITY_CHECK]
    Description: Detection of known phishing websites, malicious sites or other security risks in URLs
    Keywords: link detection, phishing website, malicious URL, website security
    Respond with an XML block containing only the extracted values:

<response>
<type>URL_SECURITY_CHECK</type>
<url></url>
</response>

Extract the necessary information(All fields present in the XML are important information) and choose the appropriate action(s) based on the text. Return the XML response following the format above.
important: do not response anything except XML`;

export const responsePrompt = (
  apiresult: string,
  text: string,
) => `You are a security action detector for blockchain interactions. Your task is to analyze the security API's response from GoPlus and summary the API result.
API to analyze:"""
${apiresult}
"""
user's request:""
${text}
""
Instructions:
1. **Identify the Action**: Analyze the API response to determine which specific action it relates to.
2. **Extract Relevant Information**: From the action and its parameters, extract and highlight the key details.
3. **Formulate a Clear Response**: Combine the action type, extracted information, and an analysis of the results. Provide a clear, concise response based on the security context. Focus on delivering the most relevant answer without unnecessary detail.
- Only reply with your conclusion.
- Do not discuss the safety aspects of the action; just focus on identifying and pointing out any risks.
- Tailor your response to the user's request, focusing on their specific query.`;
